import React from "react";

import { Button, Icon, Skeleton } from "components";

export type StatusCardCta = {
  label: string;
  onClick: () => void;
  /** "button": full-width primary button at the bottom; "circle": small round arrow in the top right corner */
  variant: "button" | "circle";
};

/** shown where a figure can't be given yet (not calculated, not available); the unit text says why */
export const NO_FIGURE = "—";

export type StatusCardProps = {
  dataTestId: string;
  label: string;
  value: string;
  /** a placeholder value (not calculated yet): shown dimmed and hidden from screen readers, which get the unit text instead */
  valueMuted?: boolean;
  unit?: string;
  /** 0–100; renders the thin progress bar when given */
  progress?: number;
  caption?: string;
  breakdown?: { label: string; value: string }[];
  cta: StatusCardCta;
  /** dark green card with white text (the payout) */
  featured?: boolean;
  loading?: boolean;
};

export const StatusCard = ({
  dataTestId,
  label,
  value,
  valueMuted = false,
  unit,
  progress,
  caption,
  breakdown,
  cta,
  featured = false,
  loading = false,
}: StatusCardProps) => {
  if (loading)
    return (
      <div data-testid={`${dataTestId}-loading`} className={`status-card-skeleton${featured ? " featured" : ""}`}>
        <Skeleton type="block" height="11rem" />
      </div>
    );

  return (
    <article data-testid={dataTestId} className={`status-card${featured ? " featured" : ""}`}>
      <div className="status-card-header">
        <span className="status-card-label">{label}</span>
        {cta.variant === "circle" ? (
          <Button dataTestId={`${dataTestId}-circle-cta`} className="status-card-circle" aria-label={cta.label} title={cta.label} onClick={cta.onClick}>
            <Icon iconName="tabler/arrow-narrow-right" size="s" />
          </Button>
        ) : null}
      </div>
      <div className="status-card-value-row">
        <span className={`status-card-value${valueMuted ? " muted" : ""}`} data-testid={`${dataTestId}-value`} aria-hidden={valueMuted || undefined}>
          {value}
        </span>
        {unit ? <span className="status-card-unit">{unit}</span> : null}
      </div>
      {progress !== undefined ? (
        <div
          className="status-card-progress-track"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <div className="status-card-progress" data-testid={`${dataTestId}-progress`} style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
        </div>
      ) : null}
      {caption ? <span className="status-card-caption">{caption}</span> : null}
      {breakdown?.length ? (
        <dl className="status-card-breakdown" data-testid={`${dataTestId}-breakdown`}>
          {breakdown.map((row) => (
            <div key={row.label} className="status-card-breakdown-row">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {cta.variant === "button" ? (
        <Button type="primary" block className="status-card-button" onClick={cta.onClick} icon={<Icon iconName="tabler/arrow-narrow-right" size="s" />}>
          {cta.label}
        </Button>
      ) : null}
    </article>
  );
};
